import { isEmpty, isPresent } from '@ember/utils';
import { tracked, cached } from '@glimmer/tracking';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import dateUtils from './date-utils';
import JournalContactInfo from './models/journal-contact-info';
import JournalReportDefault from './models/journal-report-default';
import TaskService from 'donor-elf-web/services/task-service';
import AbilitiesService from 'donor-elf-web/services/abilities';
import JournalInfo from './models/journal-info';
import JournalDashboardModel from 'donor-elf-web/models/journal-dashboard';
import JournalDigestInfo from './models/journal-digest-info';
import currency from 'currency.js';

export const goalTypeLabels = {
  1: 'Monthly',
  2: 'Annual',
  3: 'One Time',
};

export class JournalIntervalDate {
  constructor(args: {
    start: string;
    end: string;
    formatDateService: FormatDateService;
  }) {
    this.start = args.start;
    this.end = args.end;
    // TODO: look into getting rid of JournalReportDefault.getDateRangeFormatted and just
    // put the calculation in here
    this.dateRangeFormatted = JournalReportDefault.getDateRangeFormatted({
      formatDateService: args.formatDateService,
      start: args.start,
      end: args.end,
    });
  }

  start: string;
  end: string;
  dateRangeFormatted: string;

  _startDate?: Date;
  get startDate() {
    if (this._startDate) {
      return this._startDate;
    }
    return (this._startDate = dateUtils.deserializeDateOnly(this.start));
  }

  _endDate?: Date;
  get endDate() {
    if (this._endDate) {
      return this._endDate;
    }
    return (this._endDate = dateUtils.deserializeDateOnly(this.end));
  }
}

export interface IntervalCumulativeAmount {
  decision: number;
  withGift: number;
  start: string;
  end: string;
}

export interface IntervalDate {
  start: string;
  end: string;
}

interface YesDecision {
  decisionDate: string;
  firstGiftDate?: string;
  amount: number;
}

export interface JournalIntervalCumulativeDecisionSummary {
  date: JournalIntervalDate;
  cumulativeAmount: number;
  cumulativeWithGiftAmount: number;
  newAmount: number;
  newWithGiftAmount: number;
}

// yesDecisions must be for the same goal
export function CalculateIntervalCumulativeDecisionSummaries(
  intervalDates: JournalIntervalDate[],
  yesDecisions: YesDecision[],
): JournalIntervalCumulativeDecisionSummary[] {
  let intervalAmounts: {
    [date: string]: { decision: number; withGift: number };
  } = {};
  intervalDates.forEach(({ start }) => {
    intervalAmounts[start] = { decision: 0, withGift: 0 };
  });
  yesDecisions.forEach((decision) => {
    let found = {
      decision: false,
      withGift: isEmpty(decision.firstGiftDate),
    };

    for (
      let dateIndex = intervalDates.length - 1;
      dateIndex >= 0;
      dateIndex--
    ) {
      if (
        !found.decision &&
        (decision.decisionDate >= intervalDates[dateIndex].start ||
          dateIndex === 0)
      ) {
        intervalAmounts[intervalDates[dateIndex].start].decision = currency(
          intervalAmounts[intervalDates[dateIndex].start].decision,
        ).add(decision.amount).value;
        found.decision = true;
        // giftDate can't be before decisionDate
        if (!found.withGift) {
          intervalAmounts[intervalDates[dateIndex].start].withGift = currency(
            intervalAmounts[intervalDates[dateIndex].start].withGift,
          ).add(decision.amount).value;
          found.withGift = true;
        }
      }
      if (
        !found.withGift &&
        (decision.firstGiftDate! >= intervalDates[dateIndex].start ||
          dateIndex === 0)
      ) {
        intervalAmounts[intervalDates[dateIndex].start].withGift = currency(
          intervalAmounts[intervalDates[dateIndex].start].withGift,
        ).add(decision.amount).value;
        found.withGift = true;
      }
      if (found.decision && found.withGift) {
        break;
      }
    }
  });

  let cumulativeDecisionAmount = 0;
  let cumulativeWithGiftAmount = 0;
  return intervalDates.map((intervalDate) => {
    let intervalAmount = intervalAmounts[intervalDate.start];
    cumulativeDecisionAmount = currency(cumulativeDecisionAmount).add(
      intervalAmount.decision,
    ).value;
    cumulativeWithGiftAmount = currency(cumulativeWithGiftAmount).add(
      intervalAmount.withGift,
    ).value;

    return {
      date: intervalDate,
      cumulativeAmount: cumulativeDecisionAmount,
      cumulativeWithGiftAmount: cumulativeWithGiftAmount,
      newAmount: intervalAmount.decision,
      newWithGiftAmount: intervalAmount.withGift,
    };
  });
}

// yesDecisions must be for the same goal
export function GetIntervalCumulativeAmounts(
  intervalDates: IntervalDate[],
  yesDecisions: YesDecision[],
): IntervalCumulativeAmount[] {
  let intervalAmounts: any = {};
  intervalDates.forEach(({ start }) => {
    intervalAmounts[start] = { decision: 0, withGift: 0 };
  });
  yesDecisions.forEach((decision) => {
    let found = {
      decision: false,
      withGift: isEmpty(decision.firstGiftDate),
    };

    for (
      let dateIndex = intervalDates.length - 1;
      dateIndex >= 0;
      dateIndex--
    ) {
      if (
        !found.decision &&
        (decision.decisionDate >= intervalDates[dateIndex].start ||
          dateIndex === 0)
      ) {
        intervalAmounts[intervalDates[dateIndex].start].decision = currency(
          intervalAmounts[intervalDates[dateIndex].start].decision,
        ).add(decision.amount).value;
        found.decision = true;
      }
      if (
        !found.withGift &&
        (decision.firstGiftDate! >= intervalDates[dateIndex].start ||
          dateIndex === 0)
      ) {
        intervalAmounts[intervalDates[dateIndex].start].withGift = currency(
          intervalAmounts[intervalDates[dateIndex].start].withGift,
        ).add(decision.amount).value;
        found.withGift = true;
      }
      if (found.decision && found.withGift) {
        break;
      }
    }
  });

  let cumulativeDecisionAmount = 0;
  let cumulativeWithGiftAmount = 0;
  return intervalDates.map((dateInfo) => {
    cumulativeDecisionAmount = currency(cumulativeDecisionAmount).add(
      intervalAmounts[dateInfo.start].decision,
    ).value;
    cumulativeWithGiftAmount = currency(cumulativeWithGiftAmount).add(
      intervalAmounts[dateInfo.start].withGift,
    ).value;
    return {
      ...dateInfo,
      decision: cumulativeDecisionAmount,
      withGift: cumulativeWithGiftAmount,
    };
  });
}

export class JournalRowTaskInfo {
  constructor(task: V2TaskModel) {
    this.task = task;
  }

  task: V2TaskModel;
  @tracked errorSavingTask: boolean = false;
}
export class JournalRow {
  constructor(journalContactInfo: JournalContactInfo) {
    this.journalContactInfo = journalContactInfo;
    this.journalContactInfo.journalInfo.sortedStages.forEach((stage: any) => {
      this.cellState[stage.id] = {};
    });
    this.cellState['contact'] = {};
  }

  journalContactInfo: JournalContactInfo;

  taskInfosById: { [taskId: string]: JournalRowTaskInfo } = {};

  @cached
  get taskInfosByStageId(): { [stageId: string]: JournalRowTaskInfo[] } {
    let data: { [taskId: string]: JournalRowTaskInfo[] } = {};
    let { tasksByStageId } = this.journalContactInfo;

    Object.keys(tasksByStageId).forEach((stageId: string) => {
      data[stageId] = [];
      tasksByStageId[stageId]?.forEach((task: V2TaskModel) => {
        if (this.taskInfosById[task.id] == null) {
          this.taskInfosById[task.id] = new JournalRowTaskInfo(task);
        }
        data[stageId].push(this.taskInfosById[task.id]);
      });
    });
    return data;
  }

  @tracked showTasks = false;
  @tracked active = false;

  @tracked cellState: any = {};
  // call this whenever the cellState is changed so the template knows about it
  fireCellStateChanged() {
    this.cellState = { ...this.cellState };
  }
}

export class JournalStageColumn {
  constructor(props = {}) {
    Object.assign(this, props);
    this.calculateShowQuickAddButtons();
  }

  // not needed for contact column
  taskService!: TaskService;
  abilities!: AbilitiesService;

  // Ember Table Column properties
  name!: string;
  isFixed!: boolean;
  width!: number;

  // custom properties
  isContactColumn: boolean = false;
  isStageColumn: boolean = false;
  isFollowUpColumn: boolean = false;
  isDecisionColumn: boolean = false;
  isLastStage: boolean = false;
  stageId!: string;
  stageIndex!: number;
  @tracked unfinishedTasksCount: number = 0;
  @tracked finishedTasksCount: number = 0;
  @tracked decisionsCount: number = 0;
  @tracked lastTaskActionId?: string;
  @tracked lastTaskMediumId?: string;
  @tracked showCompletedQuickAddButton = false;
  @tracked showAttemptedQuickAddButton = false;

  calculateShowQuickAddButtons() {
    if (this.isContactColumn) {
      return;
    }
    this.showCompletedQuickAddButton = this.lastTaskActionId != null;
    this.showAttemptedQuickAddButton =
      this.lastTaskActionId != null &&
      this.taskService.availableResultsByActionId[this.lastTaskActionId]?.some(
        (x) => x.label === 'Attempted',
      );
  }
}

export type onJournalCellChangeArgs = {
  taskSaved?: V2TaskModel;
  taskDeleted?: V2TaskModel;
  taskPasted?: boolean;
  contactSegmentsChanged?: boolean;
  decisionChanged?: boolean;
};

export type onJournalCellChangeCallback = (
  args?: onJournalCellChangeArgs,
) => void;

export interface JournalReportDefaultCompletedStageWork {
  completed: number;
  attempted: number;
  total: number;
}

export interface JournalReportDefaultIntervalItem {
  date: string; // '2023-04-18'
  dateEnd: string; // '2023-04-18'
  dateRangeFormatted: string; // '10/18 - 10/24'
  completedStageWork: {
    [stageId: string]: JournalReportDefaultCompletedStageWork;
  }; // technically this also has the 'maxTotal' key but TypeScript won't let me know show it
  createdStageTasks: { [stageId: string]: number };
  decisions: any;
  decisionStages: any;
}

export class JournalCalculations {
  constructor(
    journalInfo: JournalInfo,
    journalContactInfos: JournalContactInfo[],
    journalDashboard: JournalDashboardModel,
  ) {
    this.journalInfo = journalInfo;
    this.journalContactInfos = journalContactInfos;
    this.journalDashboard = journalDashboard;
  }

  journalInfo: JournalInfo;
  journalContactInfos: JournalContactInfo[];
  journalDashboard: JournalDashboardModel;

  get decisionsRequireGift() {
    return this.journalInfo.journal.decisionsRequireGift;
  }

  get goal() {
    return this.journalInfo.goals[0];
  }

  get goalAmount() {
    return this.goal?.amount || 1;
  }

  get goalDate() {
    return this.journalInfo.journal.goalDate;
  }

  @cached
  get raisedAmount() {
    return this.decisionsRequireGift
      ? this.withGiftAmount
      : this.decisionAmount;
  }

  get raisedPercent() {
    return Math.round((this.raisedAmount * 100) / this.goalAmount);
  }

  @cached
  get decisionAmount() {
    let { intervalCumulativeAmounts } = this;
    return (
      intervalCumulativeAmounts[intervalCumulativeAmounts.length - 1]
        ?.decision || 0
    );
  }

  get decisionPercent() {
    return (
      Math.round((this.decisionAmount * 100) / this.goalAmount) -
      this.withGiftPercent
    );
  }

  @cached
  get withGiftAmount() {
    let { intervalCumulativeAmounts } = this;
    return (
      intervalCumulativeAmounts[intervalCumulativeAmounts.length - 1]
        ?.withGift || 0
    );
  }

  get withGiftPercent() {
    return Math.round((this.withGiftAmount * 100) / this.goalAmount);
  }

  // Array<YesDecision> [{ amount: 5.5, decisionDate: '2021-04-02', firstGiftDate: '2021-04-02' }]
  get yesDecisions(): YesDecision[] {
    let { goal } = this;
    if (goal == null) {
      return [];
    }
    let decisions: YesDecision[] = [];
    this.journalContactInfos.forEach((info) => {
      info.journalDecisions.forEach((decision) => {
        if (decision.yesNo && decision.journalGoalId === goal.id) {
          decisions.push({
            amount: decision.amount!,
            decisionDate: dateUtils.serializeToDateOnly(decision.decisionDate),
            firstGiftDate: isPresent(decision.firstGiftDate)
              ? dateUtils.serializeToDateOnly(decision.firstGiftDate)
              : null,
          });
        }
      });
    });
    return decisions;
  }

  @cached
  get intervalCumulativeAmounts() {
    if (this.goal == null) {
      return [];
    }
    let {
      yesDecisions,
      journalDashboard: { intervalDates },
    } = this;
    return GetIntervalCumulativeAmounts(intervalDates, yesDecisions);
  }
}

// Journal Group Download specific for InterVarsity
export class JournalGroupDownloadInterVarsity {
  constructor(
    journalDigestInfos: JournalDigestInfo[],
    formatDateService: FormatDateService,
  ) {
    this.journalDigestInfos = journalDigestInfos;
    this.formatDateService = formatDateService;
  }

  journalDigestInfos: JournalDigestInfo[];
  formatDateService: FormatDateService;

  generateReport(): [any[]] {
    let lastSixIntervalDateInfos = this.getLastSixIntervalDateInfos(
      this.journalDigestInfos,
    );

    let headerRow = [
      'Fund',
      'Journal Id',
      'Journal Name',
      'Start Date',
      'Goal Amount',
      'Goal Date',
      'Raised Amount',
      'Raised %',
      'Total Engages',
      'Total Asks Made',
      'Total Recommendation Asks',
      'Total Recommendations Collected',
      'Total Follow Ups',
      'Total Closed Asks',
    ];
    lastSixIntervalDateInfos.forEach((x) => {
      headerRow.push(`Engages ${x.dateFormatted.split(' ')[0]}`);
    });
    lastSixIntervalDateInfos.forEach((x) => {
      headerRow.push(`Asks Made ${x.dateFormatted.split(' ')[0]}`);
    });
    headerRow.push('Recurring');
    headerRow.push('Yes Decisions');
    headerRow.push('# Contacts');
    headerRow.push('# Contacts w Task or Decision');
    let rows: [any[]] = [headerRow];

    this.journalDigestInfos.forEach((info) => {
      let journalCalculations = new JournalCalculations(
        info.journalInfo!,
        info.journalContactInfos!,
        info.journalDashboard!,
      );
      let lastFullIntervalIndex = this.getLastFullIntervalIndex(info);
      let lastFullIntervalItem =
        info.reportIntervarsity!.intervalData[lastFullIntervalIndex];
      let lastSixFullIntervalItems = lastSixIntervalDateInfos.map((dateInfo) =>
        info.reportIntervarsity!.intervalData!.find(
          (x) => x.date == dateInfo.date,
        ),
      );

      let row: any[] = [];
      row.push(info.journalSlim.fundName);
      row.push(info.journalSlim.id);
      row.push(info.journalSlim.name);
      row.push(
        this.formatDateService.formatForFile(info.journalSlim.startDate),
      );
      row.push(journalCalculations.goalAmount);
      row.push(
        this.formatDateService.formatForFile(journalCalculations.goalDate),
      );
      row.push(journalCalculations.raisedAmount);
      row.push(journalCalculations.raisedPercent);
      row.push(
        lastFullIntervalItem?.completedTaskCounts.engagementCumulative || 0,
      );
      row.push(lastFullIntervalItem?.completedTaskCounts.askCumulative || 0);
      row.push(
        lastFullIntervalItem?.completedTaskCounts.recommendationAskCumulative ||
          0,
      );
      row.push(
        info.reportIntervarsity?.totalRecommendations.recommendationsCollected,
      );
      row.push(
        lastFullIntervalItem?.completedTaskCounts.followupCumulative || 0,
      );
      row.push(lastFullIntervalItem?.decisionCumulativeCount || 0);
      lastSixFullIntervalItems.forEach((x) => {
        row.push(x?.completedTaskCounts?.engagement);
      });
      lastSixFullIntervalItems.forEach((x) => {
        row.push(x?.completedTaskCounts?.ask);
      });
      row.push(info.reportIntervarsity?.decisionTypeAmounts.recurring);
      row.push(
        info.journalContactInfos?.filter((x) =>
          x.journalDecisions.some((y) => y.yesNo),
        ).length,
      );
      row.push(info.journalContactInfos?.length);
      row.push(
        info.journalContactInfos?.filter(
          (x) => x.journalDecisions.length > 0 || x.tasks.length > 0,
        ).length,
      );

      rows.push(row);
    });

    return rows;
  }

  getLastFullIntervalIndex(info: JournalDigestInfo) {
    let lastFullIntervalIndex =
      info.reportIntervarsity!.intervalData.length - 2;
    if (info.journalInfo!.journal.closedDate != null) {
      lastFullIntervalIndex += 1;
    }
    return lastFullIntervalIndex;
  }

  getLastSixIntervalDateInfos(
    journalDigestInfos: JournalDigestInfo[],
  ): { date: string; dateFormatted: string }[] {
    let dates: { [date: string]: { date: string; dateFormatted: string } } = {};
    journalDigestInfos.forEach((info) => {
      let lastFullInterval =
        info.reportIntervarsity!.intervalData[
          this.getLastFullIntervalIndex(info)
        ];
      info.reportIntervarsity!.intervalData.forEach((interval) => {
        if (lastFullInterval && interval.date <= lastFullInterval.date) {
          if (dates[interval.date] == null) {
            dates[interval.date] = {
              date: interval.date,
              dateFormatted: interval.dateFormatted,
            };
          }
        }
      });
    });
    let allDates = Object.keys(dates).sort();
    return allDates
      .slice(Math.max(allDates.length - 6, 0))
      .map((x) => dates[x]);
  }
}
